import React, { useCallback, useState } from 'react';
import { managementAddUserToAccountByUniqueIdentifier } from '../../core/_requests';
import { APIError, useAxios } from '../../core/_axios';
import { Account } from '../../core/_models';
import { useNavigate } from 'react-router-dom';
import { addUserToCurrentAccount } from '../../core/_requests_v2';
import useLocalizedCopy from '../../hooks/useLocalizedCopy';

export default function ManageAccountUserForm({
    account,
    isForCurrentAccount = false,
}: {
    account: Account,
    isForCurrentAccount?: boolean;
}) {
    const axios = useAxios();

    const navigate = useNavigate();
    const { copy } = useLocalizedCopy();

    const [saving, setSaving] = useState(false);
    const [errorSaving, setErrorSaving] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

    const onManageAccountFormSubmitted = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (saving) {
            return;
        }
        setSaveSuccess(null);
        setErrorSaving(null);
        setSaving(true);
        const form = event.currentTarget;
        const formData = new FormData(form);
        const data = Object.fromEntries(formData.entries());
        try {
            if (isForCurrentAccount) {
                await addUserToCurrentAccount(axios, {
                    ...data,
                });
                navigate('/manage-account/users');
            } else {
                await managementAddUserToAccountByUniqueIdentifier(axios, account.unique_identifier, {
                    ...data,
                })
                navigate(`/management/accounts/${account.unique_identifier}/users`)
            }
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : copy.Errors.generic + '. ' + copy.Common.messages.tryAgain
            setErrorSaving(errorMessage)
            setSaving(false)
        }
    }, [saving, account, axios, navigate, isForCurrentAccount]);

    return <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 lg:gap-7.5">
        <div className="col-span-2">
            <div className="flex flex-col gap-5 lg:gap-7.5">
                <form className="card" onSubmit={onManageAccountFormSubmitted}>
                    <div className="card-header">
                        <h3 className="card-title">
                            {`${copy.Account.addUser.title} - ${account.name || copy.Common.navigation.account}`}
                        </h3>
                    </div>
                    <div className="card-body grid gap-7.5 py-5 lg:py-7.5">
                        <div className="flex flex-col gap-4">
                            <div>
                                <label
                                    htmlFor="user_full_name"
                                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                    {`${copy.Common.labels.name} (${copy.Common.labels.required})`}
                                </label>
                                <input
                                    id="user_full_name"
                                    name="user_full_name"
                                    type="text"
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                    placeholder={copy.Common.labels.name}
                                    required
                                    autoComplete='off'
                                    defaultValue={''}
                                    autoFocus={true}
                                />
                                {false && 'this is a subtitle' && <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                    e.g. narthex, back chapel, viewing room, etc.
                                </div>}
                            </div>
                            {/* Email address: */}
                            <div>
                                <label
                                    htmlFor="user_email"
                                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                    {`${copy.Common.labels.email} (${copy.Common.labels.required})`}
                                </label>
                                <input
                                    id="user_email"
                                    name="user_email"
                                    type="email"
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                    placeholder={copy.Account.addUser.emailPlaceholder}
                                    required
                                    autoComplete='off'
                                    defaultValue={''}
                                    autoFocus={false}
                                />
                                {false && 'this is a subtitle' && <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                    e.g. narthex, back chapel, viewing room, etc.
                                </div>}
                            </div>
                            <div>
                                <label
                                    htmlFor="send_welcome_email"
                                    className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                                    {copy.Account.addUser.sendInvite}
                                </label>
                                <input
                                    id="send_welcome_email"
                                    name="send_welcome_email"
                                    type="checkbox"
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                    defaultChecked={true}
                                />
                                {false && 'this is a subtitle' && <div className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                                    e.g. narthex, back chapel, viewing room, etc.
                                </div>}
                            </div>
                        </div>
                    </div>
                    <div className="card-footer py-3.5 flex flex-col lg:flex-row items-center justify-between">
                        <div>
                            {errorSaving && <div className="text-red-500 text-sm font-medium">{errorSaving}</div>}
                            {saveSuccess && <div className="text-green-500 text-sm font-medium">{saveSuccess}</div>}
                        </div>
                        <div>
                            <button
                                type="submit"
                                className="btn btn-sm btn-primary"
                                disabled={saving}
                            >
                                {!saving ? copy.Account.users.addUser : copy.Account.addUser.sending}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
}